import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  KPICard,
  LoadingGrid,
  formatCurrency,
  formatHours,
  COMPARE_COLORS,
} from './statisticsShared';
import type { StatisticsOutletContext } from './statisticsShared';
import { useHideAmounts } from '@/hooks/useHideAmounts';

export default function TeamTab() {
  const { data, loading, hasFinancialData, period, preferTableView } = useOutletContext<StatisticsOutletContext>();
  const navigate = useNavigate();
  const { hideAmounts, amountClassName } = useHideAmounts();

  // ── Team comparison state ──
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [teamCompareData, setTeamCompareData] = useState<Record<number, any>>({});
  const [teamCompareLoading, setTeamCompareLoading] = useState(false);

  // Fetch detailed stats for selected team members
  const fetchTeamCompareData = useCallback(async () => {
    if (selectedTeamIds.length === 0) {
      setTeamCompareData({});
      return;
    }
    setTeamCompareLoading(true);
    try {
      const results: Record<number, any> = {};
      await Promise.all(
        selectedTeamIds.map(async (id) => {
          const res = await api.get(
            `${ROUTES.API.STATISTICS}users/${id}/?detail=full&period=${period}`,
          );
          results[id] = res.data;
        }),
      );
      setTeamCompareData(results);
    } catch {
      toast({ title: 'Error', description: 'Failed to fetch comparison data', variant: 'destructive' });
    } finally {
      setTeamCompareLoading(false);
    }
  }, [selectedTeamIds, period]);

  useEffect(() => {
    fetchTeamCompareData();
  }, [fetchTeamCompareData]);

  // Auto-select all team members when data loads
  useEffect(() => {
    if (data?.team_performance && data.team_performance.length > 0) {
      setSelectedTeamIds(data.team_performance.map((e) => e.id));
    }
  }, [data?.team_performance]);

  const toggleTeamMember = (id: number) => {
    setSelectedTeamIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div className="space-y-6">
      {/* Team Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Team Members"
          value={String(data?.kpis?.active_employees ?? 0)}
          subtitle="active this period"
          loading={loading}
        />
        <KPICard
          title="Total Hours"
          value={formatHours(data?.kpis?.billable_hours)}
          subtitle="billed by the team"
          loading={loading}
        />
        {hasFinancialData && (
          <>
            <KPICard
              title="Total Revenue"
              value={formatCurrency(data!.kpis.total_revenue)}
              loading={loading}
            />
            <KPICard
              title="Avg per Employee"
              value={formatCurrency(data!.kpis.revenue_per_employee)}
              loading={loading}
            />
          </>
        )}
      </div>

      {/* Full Team Table with checkboxes */}
      {!loading && data?.team_performance && data.team_performance.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Revenue per Employee</CardTitle>
                <CardDescription>
                  {selectedTeamIds.length === data.team_performance.length
                    ? 'All employees selected — deselect to filter the graphs'
                    : `${selectedTeamIds.length} of ${data.team_performance.length} selected`}
                </CardDescription>
              </div>
              <div className="flex gap-1">
                {selectedTeamIds.length < data.team_performance.length && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTeamIds(data!.team_performance.map((e) => e.id))}>
                    Select all
                  </Button>
                )}
                {selectedTeamIds.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTeamIds([])}>
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Hours Billed</TableHead>
                    <TableHead className="text-right">Total Hours</TableHead>
                    <TableHead className="text-right">Days Worked</TableHead>
                    {hasFinancialData && (
                      <>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Avg Rate</TableHead>
                        <TableHead className="text-right">Share</TableHead>
                      </>
                    )}
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.team_performance.map((emp) => {
                    const isSelected = selectedTeamIds.includes(emp.id);
                    const colorIdx = selectedTeamIds.indexOf(emp.id);
                    return (
                      <TableRow
                        key={emp.id}
                        className={cn(
                          'group cursor-pointer transition-colors',
                          isSelected ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/60',
                        )}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleTeamMember(emp.id)}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground font-medium tabular-nums">
                          {isSelected && (
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full mr-1.5"
                              style={{ background: COMPARE_COLORS[colorIdx % COMPARE_COLORS.length] }}
                            />
                          )}
                          {emp.rank}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/statistics/users/${emp.id}`)}>
                          <span className="font-medium text-primary underline-offset-4 group-hover:underline">{emp.name}</span>
                        </TableCell>
                        <TableCell className="text-right">{emp.hours.toFixed(1)}h</TableCell>
                        <TableCell className="text-right text-muted-foreground">{emp.total_hours.toFixed(1)}h</TableCell>
                        <TableCell className="text-right">{emp.days_worked}</TableCell>
                        {hasFinancialData && (
                          <>
                            <TableCell className={`text-right font-semibold ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(emp.revenue)}</TableCell>
                            <TableCell className={`text-right ${amountClassName}`}>{hideAmounts ? '•••' : `${formatCurrency(emp.avg_rate)}/h`}</TableCell>
                            <TableCell className="text-right">
                              <span className={cn(
                                'inline-block px-2 py-0.5 rounded-full text-xs font-medium',
                                (emp.revenue_share ?? 0) > 25
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                                  : 'bg-muted text-muted-foreground'
                              )}>
                                {(emp.revenue_share ?? 0).toFixed(1)}%
                              </span>
                            </TableCell>
                          </>
                        )}
                        <TableCell className="text-right" onClick={() => navigate(`/statistics/users/${emp.id}`)}>
                          <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-foreground transition-colors inline" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Team Comparison Section ── */}
      {selectedTeamIds.length > 0 && !loading && (() => {
        // Build comparison dataset from fetched user stats
        const compareEntries = selectedTeamIds
          .map((id, i) => {
            const emp = data?.team_performance?.find((e) => e.id === id);
            const stats = teamCompareData[id];
            return {
              id,
              name: emp?.name ?? `User ${id}`,
              stats,
              color: COMPARE_COLORS[i % COMPARE_COLORS.length],
            };
          });

        const hasMonthlyData = compareEntries.some((e) => e.stats?.monthly_breakdown?.length > 0);

        // ── Table view ──
        if (preferTableView) {
          return (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Comparison — Detailed Numbers</CardTitle>
                <CardDescription>Side-by-side performance metrics</CardDescription>
              </CardHeader>
              <CardContent>
                {teamCompareLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Metric</TableHead>
                          {compareEntries.map((e) => (
                            <TableHead key={e.id} className="text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: e.color }} />
                                {e.name}
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">Billable Hours</TableCell>
                          {compareEntries.map((e) => (
                            <TableCell key={e.id} className="text-right">{(e.stats?.time_metrics?.billable_hours_actual ?? 0).toFixed(1)}h</TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Total Hours</TableCell>
                          {compareEntries.map((e) => (
                            <TableCell key={e.id} className="text-right">{(e.stats?.time_metrics?.total_hours_actual ?? 0).toFixed(1)}h</TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Utilization</TableCell>
                          {compareEntries.map((e) => (
                            <TableCell key={e.id} className="text-right">{(e.stats?.time_metrics?.utilization_rate ?? 0).toFixed(0)}%</TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Days Worked</TableCell>
                          {compareEntries.map((e) => (
                            <TableCell key={e.id} className="text-right">{e.stats?.time_metrics?.days_worked ?? 0}</TableCell>
                          ))}
                        </TableRow>
                        {hasFinancialData && (
                          <>
                            <TableRow>
                              <TableCell className="font-medium">Revenue</TableCell>
                              {compareEntries.map((e) => (
                                <TableCell key={e.id} className={`text-right font-semibold ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(e.stats?.financial_metrics?.total_revenue_actual ?? 0)}</TableCell>
                              ))}
                            </TableRow>
                            <TableRow>
                              <TableCell className="font-medium">Avg Rate</TableCell>
                              {compareEntries.map((e) => (
                                <TableCell key={e.id} className={`text-right ${amountClassName}`}>{hideAmounts ? '•••' : `${formatCurrency(e.stats?.financial_metrics?.average_rate ?? 0)}/h`}</TableCell>
                              ))}
                            </TableRow>
                          </>
                        )}
                        <TableRow>
                          <TableCell className="font-medium">Target Progress</TableCell>
                          {compareEntries.map((e) => (
                            <TableCell key={e.id} className="text-right">{(e.stats?.target_tracking?.target_progress_percentage ?? 0).toFixed(0)}%</TableCell>
                          ))}
                        </TableRow>
                        {hasMonthlyData && (
                          <>
                            {/* Monthly breakdown rows */}
                            <TableRow>
                              <TableCell colSpan={compareEntries.length + 1} className="bg-muted/40 font-semibold text-xs uppercase tracking-wider">
                                Monthly Billable Hours
                              </TableCell>
                            </TableRow>
                            {(compareEntries[0]?.stats?.monthly_breakdown ?? []).map((m: any, mi: number) => (
                              <TableRow key={mi}>
                                <TableCell className="font-medium text-muted-foreground">{m.month_short}</TableCell>
                                {compareEntries.map((e) => {
                                  const month = e.stats?.monthly_breakdown?.[mi];
                                  return (
                                    <TableCell key={e.id} className="text-right">{(month?.billable_hours ?? 0).toFixed(1)}h</TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        }

        // ── Graph view (default) ──
        return (
          <>
            {/* Hours comparison chart */}
            {!teamCompareLoading && hasMonthlyData && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Hours Comparison — Monthly</CardTitle>
                  <CardDescription>Monthly billable hours across selected employees</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    {(() => {
                      const baseMonths = compareEntries.find((e) => e.stats?.monthly_breakdown?.length > 0)?.stats?.monthly_breakdown ?? [];
                      const merged = baseMonths.map((m: any) => {
                        const row: Record<string, number | string> = { month: m.month_short };
                        for (const e of compareEntries) {
                          const match = (e.stats?.monthly_breakdown ?? []).find((cm: any) => cm.month_short === m.month_short);
                          row[e.name.split(' ')[0]] = match?.billable_hours ?? 0;
                        }
                        return row;
                      });
                      return (
                        <LineChart data={merged}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Legend />
                          {compareEntries.map((e) => (
                            <Line
                              key={e.id}
                              type="monotone"
                              dataKey={e.name.split(' ')[0]}
                              stroke={e.color}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              name={e.name}
                            />
                          ))}
                        </LineChart>
                      );
                    })()}
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Revenue comparison chart */}
            {!teamCompareLoading && hasFinancialData && hasMonthlyData && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Revenue Comparison — Monthly</CardTitle>
                  <CardDescription>Monthly revenue across selected employees</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    {(() => {
                      const baseMonths = compareEntries.find((e) => e.stats?.monthly_breakdown?.length > 0)?.stats?.monthly_breakdown ?? [];
                      const merged = baseMonths.map((m: any) => {
                        const row: Record<string, number | string> = { month: m.month_short };
                        for (const e of compareEntries) {
                          const match = (e.stats?.monthly_breakdown ?? []).find((cm: any) => cm.month_short === m.month_short);
                          row[e.name.split(' ')[0]] = match?.revenue ?? 0;
                        }
                        return row;
                      });
                      return (
                        <LineChart data={merged}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={(v) => hideAmounts ? '•••' : `€${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value) => hideAmounts ? '•••' : formatCurrency(Number(value))} />
                          <Legend />
                          {compareEntries.map((e) => (
                            <Line
                              key={e.id}
                              type="monotone"
                              dataKey={e.name.split(' ')[0]}
                              stroke={e.color}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              name={e.name}
                            />
                          ))}
                        </LineChart>
                      );
                    })()}
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </>
        );
      })()}

      {loading && <LoadingGrid count={4} />}
    </div>
  );
}
